import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { PersonForm } from '../components/person/PersonForm';
import { PersonFormData } from '../types';
import { PersonService } from '../services/personService';
import { useAuth } from '../contexts/AuthContext';

/**
 * AddPerson Page
 *
 * Page for adding a new family member
 *
 * Features:
 * - Full PersonForm component
 * - Success/error handling
 * - Navigation back to People page
 * - Redirects to new person's profile on success
 *
 * @component
 * @returns {JSX.Element} Add Person page
 */
export const AddPerson: React.FC = () => {
  // ===== Hooks =====
  const navigate = useNavigate();
  const { authState } = useAuth();

  // ===== State =====
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ===== Event Handlers =====
  const handleBack = () => {
    navigate('/people');
  };

  const handleCancel = () => {
    navigate('/people');
  };

  const handleSubmit = async (data: PersonFormData) => {
    try {
      setLoading(true);
      setError(null);

      // Check if user is logged in
      if (!authState.user) {
        setError('You must be logged in to add a person.');
        setLoading(false);
        return;
      }

      // Create the person
      const person = await PersonService.createPerson(
        data,
        authState.user.uid,
        authState.user.displayName || authState.user.email || 'Unknown User',
        authState.user.role === 'admin'
      );

      // Navigate to the new person's profile
      navigate(`/people/${person.id}`);
    } catch (err: any) {
      console.error('Failed to create person:', err);
      setError(err.message || 'Failed to create person. Please try again.');
      setLoading(false);
    }
  };

  // ===== Render =====
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mb: 2 }}
        >
          Back to People
        </Button>

        <Typography variant="h4" component="h1" gutterBottom>
          Add Family Member
        </Typography>

        <Typography variant="body2" color="text.secondary">
          Fill in the details below to add a new person to your family tree.
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Form */}
      <Paper sx={{ p: 3 }}>
        <PersonForm
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          loading={loading}
          title="Add Person"
        />
      </Paper>
    </Container>
  );
};

export default AddPerson;
